import { http } from '@/shared/api';
import type { ApiResponse } from '@/shared/api';
import type { AssetType } from '../model/asset-types';
import { assetLibraryStore, triggerAssetRefresh } from '../model/asset-library-store';
import type { MaterialSpaceInfo } from '@/shared/config/space-store';
import { setSpaceInfo } from '@/shared/config/space-store';

export type { MaterialSpaceInfo };

export interface MaterialItem {
  id: string;
  material_id: string;
  uid: number;
  project_id: string;
  node_id?: string | number;
  node_ids?: string[];
  name: string;
  type: AssetType;
  source: 'ai' | 'upload';
  url: string;
  cover: string;
  thumb_200: string;
  thumb_1024: string;
  width: number;
  height: number;
  duration: number;
  size: number;
  size_text: string;
  mime_type: string;
  check_status?: number;
  ext: string;
  use_count: number;
  create_time: number;
  update_time: number;
  tags: string[];
  record_type?: string;
  pid?: string;
}



export interface MaterialListParams {
  project_id?: string;
  team_id?: string;
  pid?: string;
  folder?: 0 | 1;
  type?: AssetType;
  source?: 'ai' | 'upload';
  keyword?: string;
  node_ids?: string[];
  node_id?: string;
  page?: number;
  page_size?: number;
}

export interface MaterialListData {
  project_id: string;
  team_id?: string;
  pid?: string;
  node_id?: string;
  node_ids?: string[];
  type: string;
  source: string;
  keyword: string;
  folder?: 0 | 1;
  page: number;
  page_size: number;
  total: number;
  space: MaterialSpaceInfo;
  list: MaterialItem[];
}

export interface AddMaterialItem {
  name?: string;
  type?: string;
  url: string;
  cover?: string;
  node_id?: string;
  width: number;
  height: number;
  duration?: number;
  size?: number;
  mime_type?: string;
  ext?: string;
  md5?: string;
  pid?: string;
}

/**
 * 获取素材列表
 */
export function requestMaterialList(params: MaterialListParams): Promise<ApiResponse<MaterialListData>> {
  return http.post<MaterialListData>('/zs/AiCanvasMaterial/materialList', params as unknown as Record<string, unknown>).then((res) => {
    if (res.code === 1 && res.data?.space && params.project_id) {
      setSpaceInfo(res.data.space);
    }
    return res;
  });
}

/**
 * 获取素材详情
 */
export function requestMaterialDetail(projectId: string, materialId: string): Promise<ApiResponse<{ material: MaterialItem; tags: string[] }>> {
  return http.post<{ material: MaterialItem; tags: string[] }>('/zs/AiCanvasMaterial/getMaterialDetail', {
    project_id: projectId,
    material_id: materialId,
  });
}

/**
 * 上传素材入库 (已根据文档更新：单素材入库)
 */
export function requestAddMaterial(projectId: string, item: AddMaterialItem): Promise<ApiResponse<{ material: MaterialItem }>> {
  return http.post<{ material: MaterialItem }>('/zs/AiCanvasMaterial/addMaterial', {
    project_id: projectId,
    node_id: item.node_id,
    pid: item.pid,
    material: item,
  }).then((res) => {
    if (res.code === 1) {
      if (assetLibraryStore.searchType === 'node' || assetLibraryStore.searchType === 'group') {
        triggerAssetRefresh();
      }
      if ((res.data as any)?.space) {
        setSpaceInfo((res.data as any).space);
      }
    }
    return res;
  });
}

/**
 * 新建素材文件夹
 */
export function requestCreateMaterialFolder(params: {
  project_id?: string;
  team_id?: string;
  pid?: string;
  name: string;
  cover?: string;
}): Promise<ApiResponse<{ folder: MaterialItem }>> {
  return http.post<{ folder: MaterialItem }>('/zs/AiCanvasMaterial/createFolder', params as Record<string, unknown>);
}

/**
 * 移动素材/文件夹
 */
export function requestMoveMaterial(params: {
  project_id?: string;
  team_id?: string;
  material_ids?: (number | string)[] | string;
  folder_ids?: (number | string)[] | string;
  pid?: string;
}): Promise<ApiResponse<any>> {
  return http.post<any>('/zs/AiCanvasMaterial/moveMaterial', params as Record<string, unknown>);
}

/**
 * 更新素材文件夹
 */
export function requestUpdateMaterialFolder(params: {
  project_id?: string;
  team_id?: string;
  folder_id: string;
  name?: string;
  cover?: string;
}): Promise<ApiResponse<any>> {
  return http.post<any>('/zs/AiCanvasMaterial/updateFolder', params as Record<string, unknown>);
}

/**
 * 删除素材文件夹
 */
export function requestDeleteMaterialFolder(params: {
  project_id?: string;
  team_id?: string;
  folder_id: string;
}): Promise<ApiResponse<any>> {
  return http.post<any>('/zs/AiCanvasMaterial/deleteFolder', params as Record<string, unknown>);
}

/**
 * 引用素材到节点
 */
export function requestReferenceMaterial(projectId: string, materialId: string, nodeId: string): Promise<ApiResponse<{ material: MaterialItem }>> {
  return http.post<{ material: MaterialItem }>('/zs/AiCanvasMaterial/referenceMaterial', {
    project_id: projectId,
    material_id: materialId,
    node_id: nodeId,
  }).then((res) => {
    if (res.code === 1) {
      if (assetLibraryStore.searchType === 'node' || assetLibraryStore.searchType === 'group') {
        triggerAssetRefresh();
      }
      if ((res.data as any)?.space) {
        setSpaceInfo((res.data as any).space);
      }
    }
    return res;
  });
}

/**
 * 编辑素材名称
 */
export function requestUpdateMaterial(projectId: string | undefined, teamId: string | undefined, materialId: string, name: string): Promise<ApiResponse<unknown>> {
  return http.post('/zs/AiCanvasMaterial/updateMaterial', {
    project_id: projectId,
    team_id: teamId,
    material_id: materialId,
    name,
  });
}

/**
 * 更新素材标签
 */
export function requestUpdateMaterialTags(projectId: string | undefined, teamId: string | undefined, materialId: string, tags: string[]): Promise<ApiResponse<unknown>> {
  return http.post('/zs/AiCanvasMaterial/updateMaterialTags', {
    project_id: projectId,
    team_id: teamId,
    material_id: materialId,
    tags,
  });
}

/**
 * 删除素材
 */
export function requestDeleteMaterial(projectId: string | undefined, teamId: string | undefined, materialIds: string[]): Promise<ApiResponse<unknown>> {
  return http.post('/zs/AiCanvasMaterial/deleteMaterial', {
    project_id: projectId,
    team_id: teamId,
    material_ids: materialIds,
  }).then((res) => {
    if (res.code === 1 && (res.data as any)?.space) {
      setSpaceInfo((res.data as any).space);
    }
    return res;
  });
}

export interface NodeMaterialSummaryItem {
  node_id: string;
  image?: MaterialItem;
  material_count: number;
  space: number;
  repeat_use: boolean;
}

export interface NodeMaterialSummaryData {
  project_id: string;
  node_ids: string[];
  list: NodeMaterialSummaryItem[];
}

/**
 * 获取节点素材统计汇总
 */
export function requestNodeMaterialSummary(projectId: string, nodeIds: string[]): Promise<ApiResponse<NodeMaterialSummaryData>> {
  return http.post<NodeMaterialSummaryData>('/zs/AiCanvasMaterial/nodeMaterialSummary', {
    project_id: projectId,
    node_ids: nodeIds,
  });
}

export interface GetMaterialIdsByUrlsParams {
  project_id?: string;
  team_id?: string;
  urls: string[];
}

export interface GetMaterialIdsByUrlsResponse {
  team_id: string;
  project_id: string;
  material_ids: (string | null)[];
  materail_data: MaterialItem[];
}

/**
 * 通过素材URL批量获取素材ID
 */
export function requestGetMaterialIdsByUrls(params: GetMaterialIdsByUrlsParams): Promise<ApiResponse<GetMaterialIdsByUrlsResponse>> {
  return http.post<GetMaterialIdsByUrlsResponse>('/zs/AiCanvasMaterial/getMaterialIdsByUrls', params);
}
